import { originalCatalog } from "@marshmallow-duel/game-data";

import { abilityById } from "./ability-catalog";
import { nextRandom, normalizeSeed, randomInt } from "./rng";
import { EMPTY_INPUT } from "./types";

import type {
  AbilityPickupState,
  MatchEvent,
  MatchOptions,
  MatchState,
  PlayerInput,
  PlayerSlot,
  PlayerState,
  ProjectileKind,
  ProjectileState,
  RopeState,
  WorldObjectDefinition
} from "./types";

interface Rect {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

const SOLID_KINDS = new Set(["wall", "wallLeft", "wallRight", "wallBottom", "solidWall"]);
const DESTRUCTIBLE_KINDS = new Set(["wall", "wallLeft", "wallRight", "wallBottom", "solidWall", "solidRope", "solidRopeTop"]);
const ROPEABLE_KINDS = new Set(["wall", "wallLeft", "wallRight", "wallBottom", "solidWall", "solidRope", "solidRopeTop", "kickEmitter", "kickEmitterBottom", "kickEmitterLeft", "kickEmitterRight"]);
const PROJECTILE_RADIUS: Readonly<Record<ProjectileKind, number>> = {
  mine: 10,
  clusterMine: 12,
  grenade: 8,
  clusterGrenade: 10,
  flashGrenade: 8,
  fireball: 8,
  freezer: 8,
  teleporter: 8,
  hover: 8,
  bomb: 10,
  bomber: 10,
  rocket: 10,
  destroyer: 10,
  runBomb: 10,
  weasel: 10,
  net: 12,
  flame: 6,
  pistol: 3,
  laser: 1,
  kickWave: 8
};

const playerSprite = (player: PlayerState): string => {
  const pose = player.pose === "rope" ? "rope" : player.pose;
  const requested = `spr_player_${player.profile.skin}_${pose}`;
  return originalCatalog.sprites[requested] ? requested : `spr_player_1_${pose}`;
};

const rectFromSprite = (spriteName: string, x: number, y: number, facing: -1 | 1 = 1): Rect => {
  const sprite = originalCatalog.sprites[spriteName];
  if (!sprite) return { left: x - 12, right: x + 12, top: y - 23, bottom: y + 22 };
  const left = sprite.mask.left - sprite.origin.x;
  const right = sprite.mask.right - sprite.origin.x;
  return {
    left: x + (facing === 1 ? left : -right),
    right: x + (facing === 1 ? right : -left),
    top: y + sprite.mask.top - sprite.origin.y,
    bottom: y + sprite.mask.bottom - sprite.origin.y
  };
};

const playerRect = (player: PlayerState, x = player.x, y = player.y): Rect => rectFromSprite(playerSprite(player), x, y, player.facing);

const objectRect = (object: WorldObjectDefinition): Rect => {
  const spriteName = object.sprite ?? originalCatalog.objects[object.sourceObject]?.sprite;
  if (!spriteName) return { left: object.x, right: object.x + 32, top: object.y, bottom: object.y + 32 };
  return rectFromSprite(spriteName, object.x, object.y);
};

const overlaps = (left: Rect, right: Rect): boolean => left.left <= right.right && left.right >= right.left && left.top <= right.bottom && left.bottom >= right.top;
const pointDistance = (leftX: number, leftY: number, rightX: number, rightY: number): number => Math.hypot(leftX - rightX, leftY - rightY);

const activeObjects = (state: MatchState): readonly WorldObjectDefinition[] => [
  ...state.map.objects.filter(({ id }) => !state.destroyedObjectIds.includes(id)),
  ...state.temporaryWalls
];

const solidRopeAt = (state: MatchState, player: PlayerState, yOffset = 0): WorldObjectDefinition | undefined => {
  const rect = playerRect(player, player.x, player.y + yOffset);
  return activeObjects(state).find((object) => (object.kind === "solidRope" || object.kind === "solidRopeTop") && overlaps(rect, objectRect(object)));
};

const collidesSolid = (state: MatchState, player: PlayerState, x: number, y: number): boolean => {
  const rect = playerRect(player, x, y);
  return activeObjects(state).some((object) => SOLID_KINDS.has(object.kind) && overlaps(rect, objectRect(object)));
};

const isGrounded = (state: MatchState, player: PlayerState): boolean => collidesSolid(state, player, player.x, player.y + 1);

const moveAxis = (state: MatchState, player: PlayerState, delta: number, axis: "x" | "y"): void => {
  const steps = Math.max(1, Math.ceil(Math.abs(delta)));
  const amount = delta / steps;
  for (let index = 0; index < steps; index += 1) {
    const nextX = axis === "x" ? player.x + amount : player.x;
    const nextY = axis === "y" ? player.y + amount : player.y;
    if (collidesSolid(state, player, nextX, nextY)) {
      if (axis === "x") player.vx = 0;
      else player.vy = 0;
      return;
    }
    player.x = nextX;
    player.y = nextY;
  }
};

const emit = (state: MatchState, event: Omit<MatchEvent, "tick">): void => {
  state.events.push({ tick: state.tick, ...event });
};

const nextEntityId = (state: MatchState, prefix: string): string => `${prefix}-${state.nextEntityId++}`;

const randomValue = (state: MatchState): number => {
  const next = nextRandom(state.rngState);
  state.rngState = next.state;
  return next.value;
};

const randomInteger = (state: MatchState, min: number, maxInclusive: number): number => {
  const next = randomInt(state.rngState, min, maxInclusive);
  state.rngState = next.state;
  return next.value;
};

const createPlayer = (options: MatchOptions, slot: PlayerSlot): PlayerState => {
  const spawn = options.map.spawns[slot];
  if (!spawn) throw new Error(`Map ${options.map.id} is missing player ${slot}`);
  return {
    slot,
    profile: options.profiles[slot],
    x: spawn.x,
    y: spawn.y,
    vx: 0,
    vy: 0,
    facing: slot % 2 === 1 ? 1 : -1,
    pose: "standing",
    frame: 0,
    alive: true,
    moveable: false,
    lockTicks: 2,
    moving: false,
    crouched: false,
    onSolidRope: false,
    onRope: false,
    ropeId: null,
    canRope: true,
    kickCooldown: 0,
    abilityId: 0,
    abilityAmmo: 0,
    abilityContinuous: false,
    invisible: false,
    frozenTicks: 0,
    reverseTicks: 0,
    noRopeTicks: 0,
    hoverTicks: 0,
    netTicks: 0,
    teleportCooldown: 0,
    pistolTicks: 0,
    previousInput: EMPTY_INPUT
  };
};

export const createMatch = (options: MatchOptions): MatchState => {
  if (options.map.playerCount !== options.rules.playerCount) throw new Error("Map player count does not match the selected rules.");
  const players = Array.from({ length: options.rules.playerCount }, (_, index) => createPlayer(options, (index + 1) as PlayerSlot));
  return {
    mode: options.mode,
    map: options.map,
    rules: options.rules,
    tick: 0,
    phase: "countdown",
    phaseTicks: 30,
    rngState: normalizeSeed(options.seed),
    nextEntityId: 1,
    roomSpeed: 30,
    roomSpeedTicks: 0,
    flashTicks: 0,
    abilitySpawnCooldown: 0,
    abilityEmitterX: options.map.width / 2,
    abilityEmitterDirection: 1,
    players,
    pickups: [],
    projectiles: [],
    ropes: [],
    destroyedObjectIds: [],
    temporaryWalls: [],
    scores: { 1: 0, 2: 0, 3: 0, 4: 0 },
    roundWinner: null,
    matchWinner: null,
    events: []
  };
};

const killPlayer = (state: MatchState, player: PlayerState): void => {
  if (!player.alive) return;
  player.alive = false;
  player.moveable = false;
  player.vx = 0;
  player.vy = 0;
  state.ropes = state.ropes.filter(({ owner }) => owner !== player.slot);
  emit(state, { type: "death", player: player.slot, x: player.x, y: player.y, sound: "snd_death" });
};

interface ProjectileOptions {
  readonly creator: PlayerSlot | null;
  readonly kind: ProjectileKind;
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
  readonly lifetime?: number;
  readonly targetSlot?: PlayerSlot | null;
  readonly targetX?: number | null;
  readonly targetY?: number | null;
  readonly armedTicks?: number;
  readonly fuseTicks?: number;
  readonly counter?: number;
  readonly stuck?: boolean;
}

const pushProjectile = (state: MatchState, options: ProjectileOptions): ProjectileState => {
  const projectile: ProjectileState = {
    id: nextEntityId(state, "projectile"),
    kind: options.kind,
    creator: options.creator,
    x: options.x,
    y: options.y,
    vx: options.vx,
    vy: options.vy,
    lifetime: options.lifetime ?? 180,
    armedTicks: options.armedTicks ?? (options.kind === "mine" || options.kind === "clusterMine" ? 60 : 0),
    targetSlot: options.targetSlot ?? null,
    targetX: options.targetX ?? null,
    targetY: options.targetY ?? null,
    age: 0,
    fuseTicks: options.fuseTicks ?? 0,
    counter: options.counter ?? 0,
    stuck: options.stuck ?? false
  };
  state.projectiles.push(projectile);
  return projectile;
};

const addProjectile = (
  state: MatchState,
  creator: PlayerState,
  kind: ProjectileKind,
  vx: number,
  vy: number,
  lifetime = 180,
  targetSlot: PlayerSlot | null = null
): void => {
  pushProjectile(state, {
    creator: creator.slot,
    kind,
    x: creator.x,
    y: creator.y,
    vx,
    vy,
    lifetime,
    targetSlot
  });
};

const addClusterBurst = (state: MatchState, x: number, y: number, creator: PlayerSlot | null, count: number): void => {
  for (let index = 0; index < count; index += 1) {
    const angle = randomValue(state) * Math.PI * 2;
    const speed = 10;
    pushProjectile(state, { creator, kind: "grenade", x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, lifetime: 180 });
  }
  emit(state, { type: "explosion", x, y, sound: "snd_mineboom" });
};

const consumeAmmo = (player: PlayerState): void => {
  player.abilityAmmo -= 1;
  if (player.abilityAmmo <= 0) {
    player.abilityId = 0;
    player.abilityAmmo = 0;
    player.abilityContinuous = false;
  }
};

const nearestOpponent = (state: MatchState, player: PlayerState): PlayerState | null => {
  const opponents = state.players.filter((candidate) => candidate.alive && candidate.slot !== player.slot);
  opponents.sort((left, right) => pointDistance(player.x, player.y, left.x, left.y) - pointDistance(player.x, player.y, right.x, right.y));
  return opponents[0] ?? null;
};

const useAbility = (state: MatchState, player: PlayerState): void => {
  if (!player.moveable || player.onSolidRope) return;
  switch (player.abilityId) {
    case 1:
      if (isGrounded(state, player)) player.vy = -13;
      break;
    case 3:
      if (player.crouched && isGrounded(state, player)) {
        addProjectile(state, player, "mine", 0, 0, 900);
        player.abilityId = 0;
        emit(state, { type: "sound", sound: "snd_placemine" });
      }
      break;
    case 4:
      if (!player.crouched) player.vy -= 0.8;
      break;
    case 5:
      if (!player.crouched && !isGrounded(state, player)) player.vy = -1;
      break;
    case 6:
      for (let attempt = 0; attempt < 32; attempt += 1) {
        const x = 24 + randomValue(state) * (state.map.width - 48);
        const y = 24 + randomValue(state) * (state.map.height - 96);
        if (!collidesSolid(state, player, x, y)) {
          player.x = x;
          player.y = y;
          break;
        }
      }
      player.abilityId = 0;
      emit(state, { type: "teleport", player: player.slot, x: player.x, y: player.y, sound: "snd_teleport" });
      break;
    case 7: {
      const target = nearestOpponent(state, player);
      if (target) {
        const angle = Math.atan2(target.y - player.y, target.x - player.x);
        pushProjectile(state, {
          creator: player.slot,
          kind: "rocket",
          x: player.x,
          y: player.y,
          vx: Math.cos(angle) * 8,
          vy: Math.sin(angle) * 8,
          lifetime: 360,
          targetSlot: target.slot,
          targetX: target.x,
          targetY: target.y
        });
      }
      player.abilityId = 0;
      emit(state, { type: "sound", sound: "snd_launcherfire" });
      break;
    }
    case 10:
      player.invisible = !player.invisible;
      player.abilityId = player.invisible ? 100 : 10;
      break;
    case 100:
      player.invisible = false;
      player.abilityId = 10;
      break;
    case 12:
      if (!player.crouched) {
        for (const target of state.players) {
          if (!target.alive || target.slot === player.slot) continue;
          const ahead = player.facing === 1 ? target.x > player.x : target.x < player.x;
          if (ahead && Math.abs(target.y - (player.y - 18)) < 24) killPlayer(state, target);
        }
        player.abilityId = 0;
        emit(state, { type: "sound", sound: "snd_laser" });
      }
      break;
    case 13:
      if (player.crouched && isGrounded(state, player)) {
        addProjectile(state, player, "clusterMine", 0, 0, 900);
        player.abilityId = 0;
        emit(state, { type: "sound", sound: "snd_placemine" });
      }
      break;
    case 14:
      addClusterBurst(state, player.x, player.y, player.slot, 10);
      player.abilityId = 0;
      break;
    case 16:
      addProjectile(state, player, "grenade", player.facing * 12, -4, 300);
      consumeAmmo(player);
      emit(state, { type: "sound", sound: "snd_throw" });
      break;
    case 17:
      addProjectile(state, player, "flashGrenade", player.facing * 12, -4, 300);
      consumeAmmo(player);
      emit(state, { type: "sound", sound: "snd_throw" });
      break;
    case 20:
      addProjectile(state, player, "fireball", player.facing * 10, 0, 180);
      consumeAmmo(player);
      emit(state, { type: "sound", sound: "snd_fireball" });
      break;
    case 21:
      addProjectile(state, player, "clusterGrenade", player.facing * (player.moving ? 8 : 4), player.crouched ? 0 : -4, 360);
      player.abilityId = 0;
      emit(state, { type: "sound", sound: "snd_throw" });
      break;
    case 22:
      kick(state, player, 12, 12);
      player.abilityId = 0;
      break;
    case 23:
      player.vy = -8;
      break;
    case 24:
      for (let index = 0; index < 6; index += 1) {
        const angle = (240 + randomValue(state) * 60) * Math.PI / 180;
        pushProjectile(state, { creator: player.slot, kind: "grenade", x: player.x, y: player.y, vx: Math.cos(angle) * 3, vy: -Math.sin(angle) * 3, lifetime: 180 });
      }
      player.abilityId = 0;
      break;
    case 25:
      addProjectile(state, player, "bomber", player.facing * 14, -3, 300);
      player.abilityId = 0;
      emit(state, { type: "sound", sound: "snd_throw" });
      break;
    case 26:
      for (const object of activeObjects(state)) if (!state.destroyedObjectIds.includes(object.id)) state.destroyedObjectIds.push(object.id);
      state.pickups = [];
      state.flashTicks = Math.max(state.flashTicks, 50);
      for (const target of state.players.filter(({ alive }) => alive)) {
        state.temporaryWalls.push({ id: nextEntityId(state, "floor"), kind: "wall", sourceObject: "obj_wall", sprite: "spr_wall", x: target.x - 16, y: target.y + 34 });
      }
      player.abilityId = 0;
      emit(state, { type: "sound", sound: "snd_atomo" });
      break;
    case 27:
      state.roomSpeed = 10;
      state.roomSpeedTicks = 100;
      player.abilityId = 0;
      break;
    case 28:
      addProjectile(state, player, "freezer", player.facing * 10, 0, 180);
      consumeAmmo(player);
      emit(state, { type: "sound", sound: "snd_freezer" });
      break;
    case 29:
      for (const target of state.players) if (target.slot !== player.slot) setAbility(target, 0);
      player.abilityId = 0;
      break;
    case 30: {
      const target = nearestOpponent(state, player);
      if (target) {
        const angle = Math.atan2(target.y - player.y, target.x - player.x);
        pushProjectile(state, { creator: player.slot, kind: "destroyer", x: player.x, y: player.y, vx: Math.cos(angle) * 10, vy: Math.sin(angle) * 10, lifetime: 180, targetSlot: target.slot });
      }
      player.abilityId = 0;
      emit(state, { type: "sound", sound: "snd_fireball" });
      break;
    }
    case 31:
      addProjectile(state, player, "bomb", player.facing * (player.moving ? 12 : 6), player.crouched ? 0 : -4, 3600);
      player.abilityId = 0;
      emit(state, { type: "sound", sound: "snd_throw" });
      break;
    case 32:
      for (const object of activeObjects(state)) if (["wall", "wallLeft", "wallRight", "wallBottom"].includes(object.kind) && !state.destroyedObjectIds.includes(object.id)) state.destroyedObjectIds.push(object.id);
      player.abilityId = 0;
      emit(state, { type: "sound", sound: "snd_jolt" });
      break;
    case 33:
      player.abilityId = 101;
      emit(state, { type: "sound", sound: "snd_fireball" });
      break;
    case 34:
      addProjectile(state, player, "teleporter", player.facing * 12, -4, 180);
      consumeAmmo(player);
      emit(state, { type: "sound", sound: "snd_throw" });
      break;
    case 36:
      if (!player.crouched && Math.abs(player.vy) < 0.01) {
        player.pose = "pistol";
        player.lockTicks = 10;
        player.pistolTicks = 5;
        player.abilityId = 0;
      }
      break;
    case 37:
      state.destroyedObjectIds = [...new Set([...state.destroyedObjectIds, ...activeObjects(state).filter(({ kind }) => DESTRUCTIBLE_KINDS.has(kind)).map(({ id }) => id)])];
      state.temporaryWalls = [];
      for (let index = 0; index < 32; index += 1) {
        let placed = false;
        for (let attempt = 0; attempt < 64 && !placed; attempt += 1) {
          const x = randomValue(state) * (state.map.width - 32);
          const y = 64 + randomValue(state) * (state.map.height - 192);
          const object: WorldObjectDefinition = { id: nextEntityId(state, "wall"), kind: "wall", sourceObject: "obj_wall", sprite: "spr_wall", x, y };
          if (!state.players.some((target) => target.alive && overlaps(playerRect(target), objectRect(object))) && !state.temporaryWalls.some((wall) => overlaps(objectRect(wall), objectRect(object)))) {
            state.temporaryWalls.push(object);
            placed = true;
          }
        }
      }
      for (let x = 0; x < state.map.width; x += 32) state.temporaryWalls.push({ id: nextEntityId(state, "floor"), kind: "wall", sourceObject: "obj_wall", sprite: "spr_wall", x, y: state.map.height - 64 });
      player.abilityId = 0;
      emit(state, { type: "sound", sound: "snd_tloj" });
      break;
    case 39:
      state.pickups = state.pickups.filter(({ abilityId }) => abilityId !== 2);
      player.abilityId = 0;
      break;
    case 41:
      addProjectile(state, player, "hover", player.facing * 10, 0, 180);
      consumeAmmo(player);
      emit(state, { type: "sound", sound: "snd_hover" });
      break;
    case 42:
      for (const target of state.players) if (target.slot !== player.slot) target.noRopeTicks = 90;
      player.abilityId = 0;
      break;
    case 43:
      addProjectile(state, player, "runBomb", player.facing * 10, 0, 240);
      player.abilityId = 0;
      emit(state, { type: "sound", sound: "snd_throw" });
      break;
    case 44:
      if (!player.crouched && !isGrounded(state, player)) {
        player.pose = "parachute";
        player.vy = 0.25;
      }
      break;
    case 45:
      addProjectile(state, player, "weasel", player.facing * (player.moving ? 12 : 6), player.crouched ? 0 : -4, 3600);
      player.abilityId = 0;
      emit(state, { type: "sound", sound: "snd_weasel" });
      break;
    case 46:
      if (!player.crouched && !player.moving) {
        addProjectile(state, player, "net", player.facing * 12, 0, 180);
        player.abilityId = 0;
        emit(state, { type: "sound", sound: "snd_net" });
      }
      break;
    case 47:
      if (!player.crouched && !player.moving && Math.abs(player.vx) < 0.01) {
        player.pose = "magnet";
        for (const pickup of state.pickups) {
          const distance = pointDistance(player.x, player.y, pickup.x, pickup.y);
          const speed = distance <= 128 ? 4 : distance <= 256 ? 2 : 0;
          if (speed > 0 && distance > 0) {
            pickup.vx = ((player.x - pickup.x) / distance) * speed;
            pickup.vy = ((player.y - pickup.y) / distance) * speed;
          }
        }
      }
      break;
    case 48:
      if (!player.crouched) {
        pushProjectile(state, { creator: player.slot, kind: "flame", x: player.x + 12 * player.facing, y: player.y - 8, vx: player.facing * (8 + randomValue(state) * 3), vy: (randomValue(state) - 0.5) * 3, lifetime: 30 });
        consumeAmmo(player);
        if (player.abilityAmmo % 5 === 0) emit(state, { type: "sound", sound: "snd_fireball" });
      }
      break;
  }
};

const kick = (state: MatchState, player: PlayerState, power = 7, reach = 22): void => {
  if (!player.moveable || player.kickCooldown > 0) return;
  player.kickCooldown = 8;
  const centerX = player.x + player.facing * reach;
  for (const target of state.players) {
    if (!target.alive || target.slot === player.slot) continue;
    if (pointDistance(centerX, player.y, target.x, target.y) <= 28) {
      target.vx = player.facing * power;
      target.vy = target.crouched ? -8 : -1;
      target.moveable = false;
      target.lockTicks = 5;
    }
  }
  emit(state, { type: "sound", sound: "snd_kick" });
};

const fireOrCancelRope = (state: MatchState, player: PlayerState): void => {
  if (!state.rules.ropes || !player.canRope || player.noRopeTicks > 0 || player.pose === "parachute") return;
  if (player.ropeId) {
    state.ropes = state.ropes.filter(({ id }) => id !== player.ropeId);
    player.ropeId = null;
    player.onRope = false;
    return;
  }
  const angle = (90 - (player.moving ? 45 : 30) * player.facing) * Math.PI / 180;
  const rope: RopeState = {
    id: nextEntityId(state, "rope"),
    owner: player.slot,
    anchorX: player.x,
    anchorY: player.y,
    length: 150,
    attached: false,
    vx: Math.cos(angle) * 15,
    vy: -Math.sin(angle) * 15
  };
  state.ropes.push(rope);
  player.ropeId = rope.id;
  emit(state, { type: "sound", sound: "snd_rope" });
};

const setAbility = (player: PlayerState, abilityId: number): void => {
  const definition = abilityById(abilityId);
  player.abilityId = abilityId;
  player.abilityAmmo = definition?.ammo ?? 0;
  player.abilityContinuous = definition?.activation === "continuous" || abilityId === 48;
  player.invisible = false;
};

const resolveRandomAbility = (state: MatchState): number | null => {
  const pool = (state.map.fixedAbilityIds?.length ? state.map.fixedAbilityIds : state.rules.abilityIds).filter((id) => id !== 19);
  const allowed = state.rules.randomSkull ? pool : pool.filter((id) => id !== 2);
  if (allowed.length === 0) return null;
  return allowed[randomInteger(state, 0, allowed.length - 1)] ?? null;
};

const collectPickup = (state: MatchState, player: PlayerState, pickup: AbilityPickupState): void => {
  if (pickup.abilityId === 40) return;
  const abilityId = pickup.abilityId === 19 ? resolveRandomAbility(state) : pickup.abilityId;
  if (!abilityId) return;
  if (abilityId === 38) {
    for (const target of state.players) setAbility(target, 8);
  } else if (abilityId === 18 && player.abilityId === 18) {
    setAbility(player, 0);
  } else {
    setAbility(player, abilityId);
  }
  emit(state, { type: "pickup", player: player.slot, x: pickup.x, y: pickup.y, sound: "snd_getability" });
};

const updatePickups = (state: MatchState): void => {
  const kept: AbilityPickupState[] = [];
  for (const pickup of state.pickups) {
    pickup.x += pickup.vx;
    pickup.y += pickup.vy;
    if (pickup.x < 16 || pickup.x > state.map.width - 16) {
      pickup.vx *= -1;
      pickup.x = Math.max(16, Math.min(state.map.width - 16, pickup.x));
    }
    if (pickup.y < 16 || pickup.y > state.map.height - 16) {
      pickup.vy *= -1;
      pickup.y = Math.max(16, Math.min(state.map.height - 16, pickup.y));
    }
    pickup.lifetime -= 1;
    if (pickup.lifetime <= 0) continue;
    if (pickup.abilityId === 40) {
      const wall = activeObjects(state).find((object) => DESTRUCTIBLE_KINDS.has(object.kind) && overlaps({ left: pickup.x - 12, right: pickup.x + 12, top: pickup.y - 12, bottom: pickup.y + 12 }, objectRect(object)));
      if (wall) {
        state.destroyedObjectIds.push(wall.id);
        emit(state, { type: "explosion", x: pickup.x, y: pickup.y, sound: "snd_rocketballboom" });
        continue;
      }
    }
    const collector = state.players.find((player) => player.alive && overlaps(playerRect(player), { left: pickup.x - 16, right: pickup.x + 16, top: pickup.y - 16, bottom: pickup.y + 16 }));
    if (collector && pickup.abilityId !== 40) {
      collectPickup(state, collector, pickup);
      continue;
    }
    kept.push(pickup);
  }
  state.pickups = kept;
};

const updateAbilitySpawner = (state: MatchState): void => {
  state.abilityEmitterX += state.abilityEmitterDirection * 2;
  if (state.abilityEmitterX >= state.map.width - 16) state.abilityEmitterDirection = -1;
  if (state.abilityEmitterX <= 16) state.abilityEmitterDirection = 1;
  if (state.pickups.length >= state.rules.abilitiesCount || randomValue(state) >= 0.01) return;
  const pool = state.map.fixedAbilityIds?.length ? state.map.fixedAbilityIds : state.rules.abilityIds;
  if (pool.length === 0) return;
  const selected = pool[randomInteger(state, 0, pool.length - 1)];
  if (!selected) return;
  const angle = (225 + randomValue(state) * 90) * Math.PI / 180;
  const speed = 1 + randomValue(state);
  state.pickups.push({
    id: nextEntityId(state, "pickup"),
    abilityId: selected,
    x: state.abilityEmitterX,
    y: 32,
    vx: Math.cos(angle) * speed,
    vy: -Math.sin(angle) * speed,
    lifetime: 3600
  });
};

const updateRopes = (state: MatchState): void => {
  const kept: RopeState[] = [];
  for (const rope of state.ropes) {
    const owner = state.players.find(({ slot }) => slot === rope.owner);
    if (!owner?.alive || owner.noRopeTicks > 0) {
      if (owner) {
        owner.ropeId = null;
        owner.onRope = false;
      }
      continue;
    }
    if (!rope.attached) {
      rope.vy += 0.35;
      rope.anchorX += rope.vx;
      rope.anchorY += rope.vy;
      const hit = activeObjects(state).find((object) => ROPEABLE_KINDS.has(object.kind) && overlaps({ left: rope.anchorX - 2, right: rope.anchorX + 2, top: rope.anchorY - 2, bottom: rope.anchorY + 2 }, objectRect(object)));
      if (hit) {
        rope.attached = true;
        rope.vx = 0;
        rope.vy = 0;
        rope.length = Math.min(150, pointDistance(owner.x, owner.y, rope.anchorX, rope.anchorY));
        owner.onRope = true;
        emit(state, { type: "sound", sound: "snd_ropepin" });
      } else if (rope.anchorX < 0 || rope.anchorX > state.map.width || rope.anchorY < 0 || rope.anchorY > state.map.height) {
        owner.ropeId = null;
        continue;
      }
    } else {
      const distance = pointDistance(owner.x, owner.y, rope.anchorX, rope.anchorY);
      if (distance > rope.length && distance > 0) {
        const correction = distance - rope.length;
        owner.x += ((rope.anchorX - owner.x) / distance) * correction;
        owner.y += ((rope.anchorY - owner.y) / distance) * correction;
      }
    }
    kept.push(rope);
  }
  state.ropes = kept;
};

const projectileHitsWall = (state: MatchState, projectile: ProjectileState): WorldObjectDefinition | undefined => {
  const radius = PROJECTILE_RADIUS[projectile.kind];
  const rect = { left: projectile.x - radius, right: projectile.x + radius, top: projectile.y - radius, bottom: projectile.y + radius };
  return activeObjects(state).find((object) => ["wall", "wallLeft", "wallRight", "wallBottom"].includes(object.kind) && overlaps(rect, objectRect(object)));
};

const randomFreePosition = (state: MatchState, player: PlayerState): void => {
  for (let attempt = 0; attempt < 32; attempt += 1) {
    const x = 24 + randomValue(state) * (state.map.width - 48);
    const y = 24 + randomValue(state) * (state.map.height - 96);
    if (!collidesSolid(state, player, x, y)) {
      player.x = x;
      player.y = y;
      return;
    }
  }
};

const resolveProjectileCancellations = (state: MatchState): void => {
  const removed = new Set<string>();
  const core = new Set<ProjectileKind>(["fireball", "freezer", "hover"]);
  for (let leftIndex = 0; leftIndex < state.projectiles.length; leftIndex += 1) {
    const left = state.projectiles[leftIndex];
    if (!left || removed.has(left.id)) continue;
    for (let rightIndex = leftIndex + 1; rightIndex < state.projectiles.length; rightIndex += 1) {
      const right = state.projectiles[rightIndex];
      if (!right || removed.has(right.id) || left.creator === right.creator) continue;
      const elemental = (core.has(left.kind) && core.has(right.kind)) || (left.kind === "flame" && core.has(right.kind)) || (right.kind === "flame" && core.has(left.kind));
      const turretHit = (left.kind === "weasel" && core.has(right.kind)) || (right.kind === "weasel" && core.has(left.kind));
      const matchedShots = (left.kind === "net" && right.kind === "net") || (left.kind === "pistol" && right.kind === "pistol");
      if (!(elemental || turretHit || matchedShots)) continue;
      if (pointDistance(left.x, left.y, right.x, right.y) > PROJECTILE_RADIUS[left.kind] + PROJECTILE_RADIUS[right.kind] + 8) continue;
      removed.add(left.id);
      removed.add(right.id);
    }
  }
  if (removed.size > 0) state.projectiles = state.projectiles.filter(({ id }) => !removed.has(id));
};

const updateProjectiles = (state: MatchState): void => {
  const current = state.projectiles;
  state.projectiles = [];
  for (const projectile of current) {
    projectile.lifetime -= 1;
    projectile.age += 1;
    projectile.armedTicks = Math.max(0, projectile.armedTicks - 1);
    if (projectile.lifetime <= 0) {
      if (projectile.kind === "destroyer" || projectile.kind === "rocket") addClusterBurst(state, projectile.x, projectile.y, projectile.creator, 15);
      continue;
    }

    if (projectile.kind === "rocket" && projectile.targetX !== null && projectile.targetY !== null) {
      const angle = Math.atan2(projectile.targetY - projectile.y, projectile.targetX - projectile.x);
      projectile.vx = Math.cos(angle) * 8;
      projectile.vy = Math.sin(angle) * 8;
      if (Math.abs(projectile.x - projectile.targetX) < 8 && Math.abs(projectile.y - projectile.targetY) < 8) {
        addClusterBurst(state, projectile.x, projectile.y, projectile.creator, 15);
        continue;
      }
    }

    if (projectile.kind === "bomber" && projectile.age >= 5 && (projectile.age - 5) % 3 === 0) {
      pushProjectile(state, { creator: projectile.creator, kind: "grenade", x: projectile.x, y: projectile.y, vx: projectile.vx / 2, vy: projectile.vy / 2, lifetime: 240 });
    }

    if (projectile.kind === "weasel" && projectile.stuck) {
      const target = state.players.find((player) => player.alive && player.slot !== projectile.creator && pointDistance(player.x, player.y, projectile.x, projectile.y) <= 300 && Math.abs(player.y - projectile.y) < 32);
      if (projectile.fuseTicks === 0 && target) projectile.fuseTicks = 30;
      if (projectile.fuseTicks > 0) {
        projectile.fuseTicks -= 1;
        if (projectile.fuseTicks === 0) {
          projectile.counter += 1;
          if (projectile.counter >= 5 && target) {
            pushProjectile(state, { creator: projectile.creator, kind: "flame", x: projectile.x, y: projectile.y, vx: target.x < projectile.x ? -10 : 10, vy: 0, lifetime: 120 });
            projectile.counter = 1;
            emit(state, { type: "sound", sound: "snd_fireball" });
          }
        }
      }
    }

    if (projectile.kind === "bomb" && projectile.stuck && projectile.fuseTicks > 0) {
      projectile.fuseTicks -= 1;
      if (projectile.fuseTicks > 0 && projectile.fuseTicks % 30 === 0) emit(state, { type: "sound", sound: "snd_beep" });
      if (projectile.fuseTicks === 0) {
        addClusterBurst(state, projectile.x, projectile.y, projectile.creator, 15);
        continue;
      }
    }

    if (projectile.kind === "clusterGrenade" && projectile.fuseTicks > 0) {
      projectile.fuseTicks -= 1;
      if (projectile.fuseTicks === 0) {
        addClusterBurst(state, projectile.x, projectile.y, projectile.creator, 10);
        continue;
      }
    }

    if (!projectile.stuck) {
      if (["grenade", "clusterGrenade", "flashGrenade", "bomb", "teleporter", "weasel"].includes(projectile.kind)) projectile.vy += 0.5;
      else if (projectile.kind === "bomber") projectile.vy += 0.2;
      projectile.x += projectile.vx;
      projectile.y += projectile.vy;
    }

    if (projectile.kind === "mine" || projectile.kind === "clusterMine") {
      const target = state.players.find((player) => player.alive && pointDistance(player.x, player.y, projectile.x, projectile.y) <= 26);
      if (target && projectile.armedTicks === 0) {
        if (projectile.kind === "mine") killPlayer(state, target);
        else addClusterBurst(state, projectile.x, projectile.y, projectile.creator, 4);
        emit(state, { type: "explosion", x: projectile.x, y: projectile.y, sound: "snd_mineboom" });
        continue;
      }
      state.projectiles.push(projectile);
      continue;
    }

    const radius = PROJECTILE_RADIUS[projectile.kind];
    const hitsPlayers = ["fireball", "freezer", "teleporter", "hover", "destroyer", "runBomb", "net", "flame", "pistol", "kickWave"].includes(projectile.kind);
    const target = hitsPlayers ? state.players.find((player) => player.alive && player.slot !== projectile.creator && pointDistance(player.x, player.y, projectile.x, projectile.y) <= radius + 12) : undefined;
    if (target) {
      switch (projectile.kind) {
        case "freezer":
          target.frozenTicks = 90;
          target.moveable = false;
          break;
        case "teleporter": {
          randomFreePosition(state, target);
          emit(state, { type: "teleport", player: target.slot, x: target.x, y: target.y, sound: "snd_teleport" });
          state.projectiles.push(projectile);
          continue;
        }
        case "hover":
          target.hoverTicks = 90;
          target.moveable = false;
          break;
        case "net":
          target.netTicks = 60;
          target.moveable = false;
          break;
        case "kickWave":
          if (Math.abs(projectile.vx) >= Math.abs(projectile.vy)) target.vx = Math.sign(projectile.vx) * 7;
          else target.vy = Math.sign(projectile.vy) * 7;
          state.projectiles.push(projectile);
          continue;
        case "destroyer":
          killPlayer(state, target);
          state.projectiles.push(projectile);
          continue;
        case "runBomb":
          killPlayer(state, target);
          emit(state, { type: "explosion", x: projectile.x, y: projectile.y, sound: "snd_rocketballboom" });
          break;
        case "fireball":
        case "flame":
          if (target.abilityId === 101) {
            state.projectiles.push(projectile);
            continue;
          }
          killPlayer(state, target);
          break;
        case "pistol":
          killPlayer(state, target);
          break;
        case "grenade":
        case "clusterGrenade":
        case "flashGrenade":
        case "bomb":
        case "bomber":
        case "rocket":
        case "weasel":
          state.projectiles.push(projectile);
          continue;
        default:
          killPlayer(state, target);
          break;
      }
      continue;
    }

    const wall = projectileHitsWall(state, projectile);
    if (wall) {
      if (projectile.kind === "kickWave") {
        // The original moving kick field passes through the turbine geometry.
      } else if (projectile.kind === "bomb") {
        projectile.stuck = true;
        projectile.vx = 0;
        projectile.vy = 0;
        if (projectile.fuseTicks === 0) projectile.fuseTicks = 150;
      } else if (projectile.kind === "weasel") {
        projectile.stuck = true;
        projectile.vx = 0;
        projectile.vy = 0;
        if (projectile.counter === 0) projectile.counter = 1;
      } else if (projectile.kind === "clusterGrenade") {
        if (projectile.fuseTicks === 0) projectile.fuseTicks = 60;
        projectile.x -= projectile.vx;
        projectile.y -= projectile.vy;
        if (wall.kind === "wallLeft" || wall.kind === "wallRight") projectile.vx *= -0.5;
        else projectile.vy *= -0.5;
      } else if (projectile.kind === "flashGrenade") {
        state.flashTicks = Math.max(state.flashTicks, 50);
        emit(state, { type: "explosion", x: projectile.x, y: projectile.y, sound: "snd_rocketballboom" });
        continue;
      } else if (projectile.kind === "destroyer") {
        emit(state, { type: "explosion", x: projectile.x, y: projectile.y, sound: "snd_rocketballboom" });
      } else if (projectile.kind === "runBomb") {
        projectile.x -= projectile.vx;
        projectile.vx *= -1;
      } else {
        if (projectile.kind === "grenade" || projectile.kind === "bomber") emit(state, { type: "explosion", x: projectile.x, y: projectile.y, sound: "snd_rocketballboom" });
        continue;
      }
    }

    if (projectile.kind === "destroyer") {
      if (projectile.x < 8 || projectile.x > state.map.width - 8) projectile.vx *= -1;
      if (projectile.y < 8 || projectile.y > state.map.height - 8) projectile.vy *= -1;
      projectile.x = Math.max(8, Math.min(state.map.width - 8, projectile.x));
      projectile.y = Math.max(8, Math.min(state.map.height - 8, projectile.y));
    }
    if (projectile.kind === "runBomb") {
      const below = { ...projectile, y: projectile.y + 2 };
      projectile.vy = projectileHitsWall(state, below) ? 0 : 5;
    }
    if (projectile.x < -32 || projectile.x > state.map.width + 32 || projectile.y < -32 || projectile.y > state.map.height + 32) continue;
    state.projectiles.push(projectile);
  }
  resolveProjectileCancellations(state);
};

const updateWorldEmitters = (state: MatchState): void => {
  for (const emitter of activeObjects(state)) {
    if (!["kickEmitter", "kickEmitterBottom", "kickEmitterLeft", "kickEmitterRight"].includes(emitter.kind)) continue;
    const firstTick = 30 + (emitter.emitterDelayTicks ?? 0);
    const period = Math.max(1, emitter.emitterPeriodTicks ?? 90);
    if (state.tick < firstTick || (state.tick - firstTick) % period !== 0) continue;
    const vectors = emitter.kind === "kickEmitter"
      ? { x: emitter.x - 5, y: emitter.y + 48, vx: 0, vy: 4 }
      : emitter.kind === "kickEmitterBottom"
        ? { x: emitter.x - 5, y: emitter.y - 32, vx: 0, vy: -4 }
        : emitter.kind === "kickEmitterLeft"
          ? { x: emitter.x + 48, y: emitter.y + 27, vx: 4, vy: 0 }
          : { x: emitter.x - 12, y: emitter.y + 27, vx: -4, vy: 0 };
    pushProjectile(state, { creator: null, kind: "kickWave", ...vectors, lifetime: 300 });
    emit(state, { type: "sound", sound: "snd_kick" });
  }
};

const updateWorldInteractions = (state: MatchState, player: PlayerState, input: PlayerInput): void => {
  const rect = playerRect(player);
  for (const object of activeObjects(state)) {
    if (!overlaps(rect, objectRect(object))) continue;
    if (object.kind === "kicker") {
      player.vy = -12;
      player.y -= 1;
      emit(state, { type: "sound", sound: "snd_kicker" });
    } else if (object.kind === "horizontalKickerLeft") {
      player.vx = 25;
      emit(state, { type: "sound", sound: "snd_kicker" });
    } else if (object.kind === "horizontalKickerRight") {
      player.vx = -25;
      emit(state, { type: "sound", sound: "snd_kicker" });
    } else if (object.kind === "field") {
      if (input.up && player.moveable) {
        player.vy -= 1.5;
        player.crouched = false;
      }
    } else if (object.kind === "floatField") {
      if (!player.moving) {
        player.vy = -0.5;
        player.crouched = false;
      }
    } else if (object.kind === "teleport" && player.teleportCooldown === 0) {
      if ((object.targetX ?? 0) === 0 && (object.targetY ?? 0) === 0) {
        for (let attempt = 0; attempt < 32; attempt += 1) {
          const x = 24 + randomValue(state) * (state.map.width - 48);
          const y = 24 + randomValue(state) * (state.map.height - 96);
          if (!collidesSolid(state, player, x, y)) {
            player.x = x;
            player.y = y;
            break;
          }
        }
      } else {
        player.x = object.targetX ?? player.x;
        player.y = object.targetY ?? player.y;
      }
      player.teleportCooldown = 3;
      emit(state, { type: "teleport", player: player.slot, x: player.x, y: player.y, sound: "snd_teleport" });
    }
  }
};

const updatePlayer = (state: MatchState, player: PlayerState, input: PlayerInput): void => {
  if (!player.alive) return;
  const pressed = {
    up: input.up && !player.previousInput.up,
    down: input.down && !player.previousInput.down,
    shield: input.shield && !player.previousInput.shield,
    rope: input.rope && !player.previousInput.rope,
    ability: input.ability && !player.previousInput.ability
  };
  const downReleased = !input.down && player.previousInput.down;
  player.previousInput = { ...input };
  player.kickCooldown = Math.max(0, player.kickCooldown - 1);
  player.lockTicks = Math.max(0, player.lockTicks - 1);
  player.frozenTicks = Math.max(0, player.frozenTicks - 1);
  player.reverseTicks = Math.max(0, player.reverseTicks - 1);
  player.noRopeTicks = Math.max(0, player.noRopeTicks - 1);
  player.hoverTicks = Math.max(0, player.hoverTicks - 1);
  player.netTicks = Math.max(0, player.netTicks - 1);
  player.teleportCooldown = Math.max(0, player.teleportCooldown - 1);
  if (player.pistolTicks > 0) {
    player.pistolTicks -= 1;
    if (player.pistolTicks === 0) {
      pushProjectile(state, { creator: player.slot, kind: "pistol", x: player.x, y: player.y - 12, vx: 30 * player.facing, vy: 0, lifetime: 40 });
      emit(state, { type: "sound", sound: "snd_pistol" });
    }
  }
  player.canRope = player.noRopeTicks === 0;
  player.moveable = player.lockTicks === 0 && player.frozenTicks === 0 && player.hoverTicks === 0 && player.netTicks === 0;
  if (player.frozenTicks > 0 || player.netTicks > 0) {
    player.pose = player.netTicks > 0 ? "crouched" : "standing";
  }
  if (player.abilityId === 2) killPlayer(state, player);
  if (player.abilityId === 35) {
    player.frozenTicks += 90;
    player.abilityId = 0;
  }
  if (!player.alive) return;

  const reversed = player.abilityId === 18 || player.reverseTicks > 0;
  const left = reversed ? input.right : input.left;
  const right = reversed ? input.left : input.right;
  const runningSpeed = player.abilityId === 9 ? 8 : player.abilityId === 15 ? 2 : 4;
  player.moving = false;

  const currentSolidRope = solidRopeAt(state, player);
  if (player.onSolidRope && !currentSolidRope) player.onSolidRope = false;
  if (player.onSolidRope && currentSolidRope) player.x = currentSolidRope.x;

  if (player.moveable) {
    if (!player.onSolidRope && (pressed.up || pressed.down)) {
      const rope = solidRopeAt(state, player, pressed.up ? -1 : 1);
      if (rope) {
        player.onSolidRope = true;
        player.crouched = false;
        player.x = rope.x;
        player.vx = 0;
        player.vy = 0;
      }
    }
    if (player.onRope) {
      if (input.up) player.vy -= runningSpeed / 10;
      if (input.down) player.vy += runningSpeed / 10;
      if (left) player.vx -= runningSpeed / 10;
      if (right) player.vx += runningSpeed / 10;
    }
    if (player.onSolidRope) {
      player.vy = 0;
      if (input.up) {
        moveAxis(state, player, -(runningSpeed - 1), "y");
        player.moving = true;
      } else if (input.down) {
        moveAxis(state, player, runningSpeed - 1, "y");
        player.moving = true;
      }
    } else {
      if (pressed.down && isGrounded(state, player)) {
        player.crouched = true;
        player.pose = "crouched";
      }
      if (downReleased && player.crouched && !collidesSolid(state, player, player.x, player.y - 12)) player.crouched = false;
      if (pressed.up && isGrounded(state, player)) player.vy = -8;
    }
    if (!player.crouched) {
      if (left) {
        if (player.onSolidRope) player.onSolidRope = false;
        player.facing = -1;
        moveAxis(state, player, player.onRope ? -1 : -runningSpeed, "x");
        player.moving = true;
      }
      if (right) {
        if (player.onSolidRope) player.onSolidRope = false;
        player.facing = 1;
        moveAxis(state, player, player.onRope ? 1 : runningSpeed, "x");
        player.moving = true;
      }
    }
    if (pressed.shield && state.rules.shields) kick(state, player);
    if (pressed.rope) fireOrCancelRope(state, player);
    if ((player.abilityContinuous && input.ability) || (!player.abilityContinuous && pressed.ability)) useAbility(state, player);
  }

  if (player.hoverTicks > 0) player.vy = -3;
  const parachuting = player.abilityId === 44 && input.ability && !player.crouched && !isGrounded(state, player);
  const grounded = isGrounded(state, player);
  if (!grounded && !player.onSolidRope && !parachuting) player.vy = Math.min(18, player.vy + 0.5);
  if (grounded && player.vy > 0) player.vy = 0;
  if (Math.abs(player.vx) > 0.5) player.vx *= 0.9;
  else player.vx = 0;
  moveAxis(state, player, player.vx, "x");
  moveAxis(state, player, player.vy, "y");
  if (isGrounded(state, player) && player.ropeId) {
    state.ropes = state.ropes.filter(({ id }) => id !== player.ropeId);
    player.ropeId = null;
    player.onRope = false;
  }
  player.x = Math.max(24, Math.min(state.map.width - 24, player.x));
  if (player.y - 24 <= 0) {
    player.y = 25;
    player.vy = 0;
  }
  if (player.abilityId === 8 && player.y > state.map.height - 48) {
    player.y = state.map.height - 48;
    player.vy = 0;
  } else if (player.y > state.map.height - 48) killPlayer(state, player);

  if (player.alive) {
    updateWorldInteractions(state, player, input);
    if (player.crouched || player.netTicks > 0) player.pose = "crouched";
    else if (player.onSolidRope) player.pose = player.moving ? "roping" : "rope";
    else if (!isGrounded(state, player)) player.pose = parachuting ? "parachute" : "falling";
    else player.pose = player.moving ? "running" : "standing";
    player.frame = player.pose === "running" || player.pose === "roping" ? (player.frame + 0.5) % (player.pose === "running" ? 8 : 10) : 0;
  }
};

const resolvePlayerContacts = (state: MatchState): void => {
  for (let leftIndex = 0; leftIndex < state.players.length; leftIndex += 1) {
    const left = state.players[leftIndex];
    if (!left?.alive) continue;
    for (let rightIndex = leftIndex + 1; rightIndex < state.players.length; rightIndex += 1) {
      const right = state.players[rightIndex];
      if (!right?.alive || !overlaps(playerRect(left), playerRect(right))) continue;
      if (left.abilityId === 11 || left.abilityId === 101) {
        if (right.abilityId === 11 || right.abilityId === 101) right.abilityId = 0;
        else killPlayer(state, right);
        left.abilityId = 0;
      }
      if (right.alive && (right.abilityId === 11 || right.abilityId === 101)) {
        if (left.abilityId === 11 || left.abilityId === 101) left.abilityId = 0;
        else killPlayer(state, left);
        right.abilityId = 0;
      }
      if (state.rules.physics && left.alive && right.alive) {
        const direction = left.x <= right.x ? 1 : -1;
        left.vx = -direction * 7;
        right.vx = direction * 7;
        left.lockTicks = 5;
        right.lockTicks = 5;
      }
    }
  }
};

const finishRoundIfNeeded = (state: MatchState): void => {
  if (state.phase !== "playing") return;
  const alive = state.players.filter(({ alive }) => alive);
  if (alive.length > 1) return;
  if (state.phaseTicks === 0) state.phaseTicks = 60;
  state.phaseTicks -= 1;
  if (state.phaseTicks > 0) return;
  const winner = alive[0]?.slot ?? null;
  if (!winner) {
    state.roundWinner = null;
    state.matchWinner = null;
    state.phase = "roundOver";
    emit(state, { type: "roundWinner" });
    return;
  }
  state.roundWinner = winner;
  state.scores[winner] += 1;
  state.phase = state.scores[winner] >= state.rules.winnerPoints ? "matchOver" : "roundOver";
  state.matchWinner = state.phase === "matchOver" ? winner : null;
  emit(state, { type: state.phase === "matchOver" ? "matchWinner" : "roundWinner", player: winner });
};

export const stepMatch = (state: MatchState, inputs: Readonly<Partial<Record<PlayerSlot, PlayerInput>>>): MatchState => {
  state.events = [];
  state.tick += 1;
  if (state.phase === "countdown") {
    state.phaseTicks -= 1;
    if (state.phaseTicks <= 0) {
      state.phase = "playing";
      state.phaseTicks = 0;
    }
    return state;
  }
  if (state.phase !== "playing") return state;
  state.flashTicks = Math.max(0, state.flashTicks - 1);
  if (state.roomSpeedTicks > 0) {
    state.roomSpeedTicks -= 1;
    if (state.roomSpeedTicks === 0) state.roomSpeed = 30;
  }
  updateAbilitySpawner(state);
  updatePickups(state);
  updateRopes(state);
  updateWorldEmitters(state);
  for (const player of state.players) updatePlayer(state, player, inputs[player.slot] ?? EMPTY_INPUT);
  resolvePlayerContacts(state);
  updateProjectiles(state);
  finishRoundIfNeeded(state);
  return state;
};

export const restartRound = (state: MatchState, seed = state.rngState): MatchState => {
  const profiles = Object.fromEntries(state.players.map((player) => [player.slot, player.profile])) as Record<PlayerSlot, PlayerState["profile"]>;
  const next = createMatch({ mode: state.mode, map: state.map, rules: state.rules, profiles, seed });
  next.scores = { ...state.scores };
  return next;
};

export const serializeMatchState = (state: MatchState): string => JSON.stringify(state);
export const deserializeMatchState = (serialized: string): MatchState => JSON.parse(serialized) as MatchState;
